import PropTypes from "prop-types";
import { Alert, Avatar, Box, Typography } from "../../../themeV2";
import { ProductImage } from "../../../components/Product";
import PurchaseAddons from "../../../components/PurchaseAddons";
import PurchasedServices from "../../../components/PurchasedServices";
import { AvChip, FulfillerWrap, ItemRow } from "./styles";

const toProductLike = (item) => ({
  aspectRatio: item.productAspectRatio,
  imageUrl: item.productImageUrl,
  name: item.productName,
  shouldContainImage: item.productAspectRatio !== 1,
  signedType: item.signedType,
});

export default function ShipmentItem({ order, item }) {
  return (
    <ItemRow key={item.autographPurchaseId}>
      <div>
        {item.imageApprovalStatus === "pending" && (
          <div style={{ marginBottom: "var(--space-2)" }}>
            <Alert severity="info">
              Your uploaded image is under review. You&apos;ll receive an email
              if any changes are needed.
            </Alert>
          </div>
        )}

        {item.imageApprovalStatus === "rejected" && (
          <div style={{ marginBottom: "var(--space-2)" }}>
            <Alert severity="warning">
              Your image was not approved. Please check your email for a link to
              upload a new image.
            </Alert>
          </div>
        )}

        <div style={{ marginBottom: "var(--space-2)", width: 180 }}>
          <ProductImage product={toProductLike(item)} isDark={false} isSmall />
        </div>

        <Typography as="p" style={{ marginBottom: "var(--space-1)" }}>
          <strong>{item.productName}</strong>
        </Typography>

        <Typography
          variant="body-lg"
          color="variant"
          as="p"
          style={{ marginBottom: "var(--space-1)" }}
        >
          {item.productType}
        </Typography>

        {item.personalizationTo && (
          <Typography
            variant="body-lg"
            color="variant"
            as="p"
            style={{ marginBottom: "var(--space-1)" }}
          >
            <strong>Make it out to:</strong> {item.personalizationTo}
          </Typography>
        )}

        {item.notes && (
          <Typography
            variant="body-lg"
            color="variant"
            as="p"
            style={{ marginBottom: "var(--space-1)" }}
          >
            <strong>Special instructions:</strong> {item.notes}
          </Typography>
        )}

        {item.giftDetails?.recipientEmail && (
          <Typography
            variant="body-lg"
            color="variant"
            as="p"
            style={{ marginBottom: "var(--space-1)" }}
          >
            <strong>Recipient Email:</strong> {item.giftDetails.recipientEmail}
          </Typography>
        )}

        <Typography variant="label-md" color="variant" as="span">
          Item # {item.orderNumber}
        </Typography>

        {(item.services ?? []).length > 0 && (
          <div style={{ marginTop: "var(--space-2)" }}>
            <PurchasedServices services={item.services} />
          </div>
        )}
      </div>

      <div style={{ flexGrow: 1 }}>
        {item.expectedSigningText ? (
          <Typography
            variant="body-lg"
            as="p"
            style={{ marginBottom: "var(--space-1)" }}
          >
            {item.expectedSigningText}
          </Typography>
        ) : null}

        <Typography
          variant="title-md"
          as="p"
          style={{ marginBottom: "var(--space-1)" }}
        >
          Signed by
        </Typography>

        <Box
          align="flex-start"
          justify="space-between"
          gap={1}
          style={{ marginBottom: "var(--space-4)" }}
        >
          <FulfillerWrap>
            {(item.fulfillers || []).map((fulfiller) => (
              <AvChip
                key={fulfiller.purchaseFulfillmentId}
                avatar={
                  <Avatar
                    src={fulfiller.avatarUrl}
                    alt={fulfiller.name}
                    size="sm"
                  />
                }
                label={fulfiller.name}
                variant="default"
                color={fulfiller.fulfilled ? "primary" : "default"}
              />
            ))}
          </FulfillerWrap>
        </Box>

        {order.redacted ? null : (
          <PurchaseAddons
            autographPurchaseId={item.autographPurchaseId}
            receiptNumber={order.receiptNumber}
          />
        )}
      </div>
    </ItemRow>
  );
}

ShipmentItem.propTypes = {
  order: PropTypes.shape({
    redacted: PropTypes.bool,
    currency: PropTypes.string.isRequired,
    receiptNumber: PropTypes.string.isRequired,
  }).isRequired,
  item: PropTypes.shape({
    autographPurchaseId: PropTypes.number.isRequired,
    productName: PropTypes.string.isRequired,
    productSubtext: PropTypes.string,
    productType: PropTypes.string.isRequired,
    productImageUrl: PropTypes.string.isRequired,
    productAspectRatio: PropTypes.number.isRequired,
    price: PropTypes.number.isRequired,
    orderNumber: PropTypes.string.isRequired,
    certOfAuthPrice: PropTypes.number,
    framePrice: PropTypes.number,
    videoMessagePrice: PropTypes.number,
    vipAddonPrice: PropTypes.number,
    isVip: PropTypes.bool,
    hasCertOfAuth: PropTypes.bool,
    hasFrameAddon: PropTypes.bool,
    hasVideoMessage: PropTypes.bool,
    signed: PropTypes.bool,
    personalizationTo: PropTypes.string,
    notes: PropTypes.string,
    signedType: PropTypes.string.isRequired,
    expectedSigningText: PropTypes.string,
    imageApprovalStatus: PropTypes.string,
    giftDetails: PropTypes.shape({
      recipientEmail: PropTypes.string,
    }),
    fulfillers: PropTypes.arrayOf(
      PropTypes.shape({
        purchaseFulfillmentId: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        avatarUrl: PropTypes.string,
      }),
    ),
    services: PropTypes.arrayOf(
      PropTypes.shape({
        purchaseServiceId: PropTypes.number,
        label: PropTypes.string,
        customerText: PropTypes.string,
        refunded: PropTypes.bool,
      }),
    ),
  }).isRequired,
};
